import { BaseLRUStore } from 'generic/baseLRUStore';
import { Dispatch } from '@reduxjs/toolkit';
import { removeWellDropImage } from 'redux/reducers/wellReducer';
import { ImageTuningValues } from 'business/models/ImageTuningModel';

type WellDropImageStoreItem = {
  original?: HTMLImageElement;
  tuned?: HTMLImageElement;
  tuningValues?: ImageTuningValues;
  originalByteSize?: number;
  tunedByteSize?: number;
};

export const DEFAULT_WELL_DROP_IMAGE_CACHE_MAX_SIZE_MB = 2048;
const MAX_CACHE_ENTRIES = 200;

const mbToBytes = (mb: number): number => mb * 1024 * 1024;

const calculateWellDropImageStoreItemSize = (item: WellDropImageStoreItem): number => {
  const originalSize = item.originalByteSize ?? 0;
  if (!item.tuned || item.tuned === item.original) {
    return originalSize || 1;
  }

  const tunedSize = item.tunedByteSize ?? 0;
  return originalSize + (tunedSize || originalSize) || 1;
};

const releaseImageElement = (image?: HTMLImageElement, activeSrcs?: Set<string>) => {
  if (!image?.src || activeSrcs?.has(image.src)) {
    return;
  }

  if (image.src.startsWith('blob:')) {
    URL.revokeObjectURL(image.src);
  }

  image.onload = null;
  image.onerror = null;
  image.src = '';
};

const releaseWellDropImageStoreItem = (
  item?: WellDropImageStoreItem,
  current?: WellDropImageStoreItem,
) => {
  if (!item) {
    return;
  }

  const activeSrcs = new Set(
    [current?.original?.src, current?.tuned?.src].filter(Boolean),
  );
  releaseImageElement(item.original, activeSrcs);
  if (item.tuned && item.tuned !== item.original) {
    releaseImageElement(item.tuned, activeSrcs);
  }
};

class WellDropImageStore extends BaseLRUStore<string, WellDropImageStoreItem> {
  private static _instance: WellDropImageStore;
  private dispatch?: Dispatch;

  private constructor() {
    super({
      maxEntries: MAX_CACHE_ENTRIES,
      maxTotalSize: mbToBytes(DEFAULT_WELL_DROP_IMAGE_CACHE_MAX_SIZE_MB),
      sizeCalculation: calculateWellDropImageStoreItemSize,
    });
  }

  public static getInstance(): WellDropImageStore {
    if (!WellDropImageStore._instance) {
      WellDropImageStore._instance = new WellDropImageStore();
    }
    return WellDropImageStore._instance;
  }

  public injectDispatch(dispatch: Dispatch) {
    if (!this.dispatch) {
      this.dispatch = dispatch;
    }
  }

  public configureMaxTotalSizeMB(maxSizeMB: number) {
    if (maxSizeMB > 0) {
      this.setMaxTotalSize(mbToBytes(maxSizeMB));
    }
  }

  protected override onEvict(key: string, value: WellDropImageStoreItem) {
    const current = this.get(key);
    releaseWellDropImageStoreItem(value, current);

    if (current) {
      return;
    }

    if (!this.dispatch) {
      console.warn('Dispatch function is not set. Cannot remove image from store.');
      return;
    }

    this.dispatch(removeWellDropImage({ key }));
  }

  public replaceTunedImage(
    key: string,
    tunedImage: HTMLImageElement,
    tuningValues: ImageTuningValues,
    tunedByteSize?: number,
  ) {
    const item = this.get(key);
    if (item) {
      if (item.tuned && item.tuned !== item.original && item.tuned !== tunedImage) {
        releaseImageElement(item.tuned);
      }
      item.tuned = tunedImage;
      item.tuningValues = tuningValues;
      if (tunedByteSize) {
        item.tunedByteSize = tunedByteSize;
      }
      this.set(key, item);
    } else {
      console.warn(`No item found for key: ${key}`);
    }
  }
}

export default WellDropImageStore.getInstance() as WellDropImageStore;
